import React, { useState } from 'react';
import './LandingPage.css';

interface LandingPageProps {
  onLoginClick?: () => void;
  onSignUpClick?: () => void;
}

const featureCards = [
  {
    title: 'Trip Management',
    text: 'Organize every itinerary, destination, and travel note in one place.',
    image: 'https://images.unsplash.com/photo-1500534314209-a25ddb2bd429?auto=format&fit=crop&w=640&q=80',
  },
  {
    title: 'Experience Sharing',
    text: 'Share photos, route notes, and favorite places with other explorers.',
    image: 'https://images.unsplash.com/photo-1500530855697-b586d89ba3ee?auto=format&fit=crop&w=640&q=80',
  },
  {
    title: 'AI-Powered Support',
    text: 'Ask for better routes, hidden gems, budget ideas, and day-by-day plans.',
    image: 'https://images.unsplash.com/photo-1507034589631-9433cc6bc453?auto=format&fit=crop&w=640&q=80',
  },
  {
    title: 'Smart Ideas Search',
    text: 'Find destinations by mood, interests, season, or travel style.',
    image: 'https://images.unsplash.com/photo-1516483638261-f4dbaf036963?auto=format&fit=crop&w=640&q=80',
  },
];

const googleFeatures = ['Live Traffic', 'Satellite View', 'Street View', 'Places API'];

const reasons = [
  {
    title: 'Smart Route Planning',
    text: 'Wayplume delivers intelligent route optimization with real-time traffic updates, helping you reach your destination faster while discovering hidden gems along the way.',
  },
  {
    title: 'Personalized Recommendations',
    text: 'Get custom ideas for attractions, restaurants, scenic stops, and activities based on your travel style.',
  },
  {
    title: 'Offline Navigation',
    text: 'Keep your plan close even when signal drops, with saved trip details and route context.',
  },
  {
    title: 'Community & Sharing',
    text: 'Turn your journeys into inspiration for friends and other travelers.',
  },
];

const testimonials = [
  {
    quote: 'Wayplume has transformed how I plan my road trips. The route optimization is incredible and I discovered places I would have missed.',
    name: 'Sarah Johnson',
    role: 'Travel Blogger',
  },
  {
    quote: 'The personalized recommendations are spot-on and the offline maps are a lifesaver.',
    name: 'Michael Chen',
    role: 'Adventure Photographer',
  },
  {
    quote: 'The best route planning app for exploring new destinations. Smart navigation, community features, and beautiful interface.',
    name: 'Emily Rodriguez',
    role: 'Digital Nomad',
  },
];

const destinations = [
  {
    category: 'Road Trip',
    title: 'Ultimate Cross-Country Adventure: Coast to Coast in Style',
    image: 'https://images.unsplash.com/photo-1469854523086-cc02fe5d8800?auto=format&fit=crop&w=640&q=80',
  },
  {
    category: 'City Tour',
    title: 'Hidden Gems in Historic European Cities You Must Visit',
    image: 'https://images.unsplash.com/photo-1524231757912-21f4fe3a7200?auto=format&fit=crop&w=640&q=80',
  },
  {
    category: 'Nature',
    title: 'National Parks Explorer: Best Scenic Routes and Trails',
    image: 'https://images.unsplash.com/photo-1501785888041-af3ef285b470?auto=format&fit=crop&w=640&q=80',
  },
  {
    category: 'Coastal',
    title: 'Mediterranean Coastline: Stunning Beaches and Villages',
    image: 'https://images.unsplash.com/photo-1507525428034-b723cf961d3e?auto=format&fit=crop&w=640&q=80',
  },
  {
    category: 'Mountain',
    title: 'Alpine Adventures: Mountain Passes and Valley Views',
    image: 'https://images.unsplash.com/photo-1506905925346-21bda4d32df4?auto=format&fit=crop&w=640&q=80',
  },
  {
    category: 'Cultural',
    title: 'Heritage Route: Ancient Sites and Cultural Treasures',
    image: 'https://images.unsplash.com/photo-1513581166391-887a96ddeafd?auto=format&fit=crop&w=640&q=80',
  },
  {
    category: 'Adventure',
    title: 'Off-Road Expedition: Remote Destinations Worth the Journey',
    image: 'https://images.unsplash.com/photo-1500534314209-a25ddb2bd429?auto=format&fit=crop&w=640&q=80',
  },
  {
    category: 'Culinary',
    title: "Food Lover's Tour: Best Restaurants Along Scenic Routes",
    image: 'https://images.unsplash.com/photo-1504674900247-0877df9cc836?auto=format&fit=crop&w=640&q=80',
  },
];

const LandingPage: React.FC<LandingPageProps> = ({
  onLoginClick = () => {},
  onSignUpClick = () => {},
}) => {
  const [openReason, setOpenReason] = useState(0);
  const [contactForm, setContactForm] = useState({
    name: '',
    email: '',
    phone: '',
    subject: '',
    message: '',
  });

  const updateContactField = (field: keyof typeof contactForm, value: string) => {
    setContactForm((current) => ({ ...current, [field]: value }));
  };

  const handleContactSubmit = (event: React.FormEvent) => {
    event.preventDefault();
  };

  return (
    <div className="landing-page">
      <header className="landing-nav">
        <a className="landing-nav__brand" href="#home" aria-label="Wayplume home">
          <span className="landing-nav__mark">W</span>
          <span>Wayplume</span>
        </a>
        <nav className="landing-nav__links" aria-label="Landing page navigation">
          <a href="#home">Home</a>
          <a href="#offer">About Us</a>
          <a href="#destinations">Pages</a>
          <a href="#contact">Contact</a>
        </nav>
        <div className="landing-nav__actions">
          <button type="button" onClick={onLoginClick}>Log In</button>
          <button type="button" onClick={onSignUpClick}>Sign Up</button>
        </div>
      </header>

      <main>
        <section className="landing-hero" id="home">
          <div className="landing-hero__content">
            <p>Where do you want to go today?</p>
            <h1>Discover Your Perfect Route</h1>
            <span>
              Plan unforgettable journeys with intelligent route optimization and personalized recommendations.
              Join thousands of travelers discovering new adventures with Wayplume.
            </span>
            <button type="button" onClick={onSignUpClick}>Start Planning</button>
          </div>
          <div className="landing-hero__platforms" aria-label="Wayplume platform features">
            {['Plan Your Journey With:', 'iOS App', 'Android App', 'Web App', 'GPS Navigation'].map((item) => (
              <span key={item}>{item}</span>
            ))}
          </div>
        </section>

        <section className="landing-section landing-offer" id="offer">
          <SectionIntro eyebrow="What we offer" title="Everything You Need for the Perfect Journey" centered />
          <div className="landing-offer__grid">
            {featureCards.map((feature) => (
              <article key={feature.title} className="landing-offer-card">
                <img src={feature.image} alt="" />
                <div>
                  <h3>{feature.title}</h3>
                  <p>{feature.text}</p>
                  <a href="#contact">Learn More</a>
                </div>
              </article>
            ))}
          </div>
        </section>

        <section className="landing-maps">
          <div className="landing-maps__content">
            <h2>Powered by Google Maps, Built for Explorers</h2>
            <p>
              Wayplume harnesses the power of Google Maps to deliver accurate, real-time navigation and intelligent
              trip planning. Discover points of interest, get live traffic updates, and explore detailed maps with
              satellite imagery.
            </p>
            <button type="button" onClick={onSignUpClick}>Start Planning</button>
            <div className="landing-maps__chips">
              {googleFeatures.map((feature) => <span key={feature}>{feature}</span>)}
            </div>
          </div>
          <div className="landing-maps__visual">
            <img src="/landscape_map.png" alt="Map preview" />
            <div className="landing-maps__stats">
              <strong>10M+</strong>
              <span>Routes Planned</span>
              <strong>4.8</strong>
              <span>App Rating</span>
            </div>
          </div>
        </section>

        <section className="landing-section landing-why">
          <div>
            <p className="landing-eyebrow">Why choose us?</p>
            <h2>Why Settle? Join the Journey Masters</h2>
            <p>
              Embark on unforgettable adventures with Wayplume, where every journey is optimized for the best
              experience. Here are four compelling reasons to start your adventure with us.
            </p>
          </div>
          <img
            className="landing-why__image"
            src="https://images.unsplash.com/photo-1500530855697-b586d89ba3ee?auto=format&fit=crop&w=720&q=80"
            alt=""
          />
          <div className="landing-why__accordion">
            {reasons.map((reason, index) => (
              <article key={reason.title} className={openReason === index ? 'is-open' : ''}>
                <button type="button" onClick={() => setOpenReason(openReason === index ? -1 : index)}>
                  <span>{reason.title}</span>
                  <span>{openReason === index ? '-' : '+'}</span>
                </button>
                {openReason === index && <p>{reason.text}</p>}
              </article>
            ))}
          </div>
        </section>

        <section className="landing-section landing-testimonials">
          <SectionIntro
            title="Real Stories From Real Travelers"
            subtitle="What adventurers are saying about Wayplume"
            centered
          />
          <div className="landing-testimonials__grid">
            {testimonials.map((testimonial) => (
              <article key={testimonial.name}>
                <p>"{testimonial.quote}"</p>
                <div>
                  <span>{testimonial.name.charAt(0)}</span>
                  <div>
                    <strong>{testimonial.name}</strong>
                    <small>{testimonial.role}</small>
                  </div>
                </div>
              </article>
            ))}
          </div>
        </section>

        <section className="landing-section landing-destinations" id="destinations">
          <SectionIntro
            title="Explore Trending Destinations: New Routes Weekly"
            subtitle="Discover the most popular destinations and routes this week"
            centered
          />
          <div className="landing-destinations__grid">
            {destinations.map((destination) => (
              <article key={destination.title}>
                <img src={destination.image} alt="" />
                <div>
                  <span>{destination.category}</span>
                  <h3>{destination.title}</h3>
                </div>
              </article>
            ))}
          </div>
        </section>

        <section className="landing-contact" id="contact">
          <div className="landing-contact__info">
            <h2>Get in Touch</h2>
            <p>Have a question about planning your next trip? Our team is here to help you map out the perfect route.</p>
            <ContactItem title="Studio Address" value="148 Harbor View Lane, San Francisco, CA" />
            <ContactItem title="Call Us" value="+1 (415) 555-0134" />
            <ContactItem title="Mail Us" value="hello@wayplume.com" />
          </div>
          <form className="landing-contact__form" onSubmit={handleContactSubmit}>
            <input value={contactForm.name} onChange={(event) => updateContactField('name', event.target.value)} placeholder="Name" />
            <input value={contactForm.email} onChange={(event) => updateContactField('email', event.target.value)} placeholder="Email" type="email" />
            <input value={contactForm.phone} onChange={(event) => updateContactField('phone', event.target.value)} placeholder="Phone Number" />
            <input value={contactForm.subject} onChange={(event) => updateContactField('subject', event.target.value)} placeholder="Subject" />
            <textarea value={contactForm.message} onChange={(event) => updateContactField('message', event.target.value)} placeholder="Your message" rows={5} />
            <button type="submit">Send Message</button>
          </form>
        </section>

        <section className="landing-cta">
          <h2>Ready to Plan Your Next Adventure?</h2>
          <p>Join thousands of travelers using Wayplume to build smarter itineraries, share trips with friends, and explore with confidence.</p>
          <button type="button" onClick={onSignUpClick}>Sign Up Free</button>
          <span>Or get in touch with us.</span>
        </section>
      </main>

      <footer className="landing-footer">
        <div className="landing-footer__brand">
          <h3>Wayplume</h3>
          <p>Your intelligent journey planning companion. Discover new adventures, optimize your routes, and explore the world with confidence.</p>
        </div>
        <FooterColumn title="Page List" items={['Routes', 'Trips', 'About Us', 'Contact', 'Blog']} />
        <FooterColumn title="Useful Links" items={['Style Guide', 'Terms & Conditions', 'Privacy Policy', 'Support']} />
        <FooterColumn title="Work Hours" items={['Mon - Fri: 9AM - 6PM', 'Sat: 10AM - 2PM', 'Sun: Closed']} />
        <p className="landing-footer__copyright">© 2026 Wayplume. All rights reserved.</p>
      </footer>
    </div>
  );
};

interface SectionIntroProps {
  eyebrow?: string;
  title: string;
  subtitle?: string;
  centered?: boolean;
}

const SectionIntro: React.FC<SectionIntroProps> = ({ eyebrow, title, subtitle, centered = false }) => (
  <div className={`landing-section-intro ${centered ? 'landing-section-intro--center' : ''}`}>
    {eyebrow && <p>{eyebrow}</p>}
    <h2>{title}</h2>
    {subtitle && <span>{subtitle}</span>}
  </div>
);

const ContactItem: React.FC<{ title: string; value: string }> = ({ title, value }) => (
  <div className="landing-contact__item">
    <span />
    <div>
      <strong>{title}</strong>
      <p>{value}</p>
    </div>
  </div>
);

// Footer entries that lead somewhere real; the rest are still placeholders.
const FOOTER_LINKS: Record<string, string> = {
  'Privacy Policy': '/privacy',
};

const FooterColumn: React.FC<{ title: string; items: string[] }> = ({ title, items }) => (
  <div className="landing-footer__column">
    <h4>{title}</h4>
    {items.map((item) =>
      FOOTER_LINKS[item] ? <a key={item} href={FOOTER_LINKS[item]}>{item}</a> : <span key={item}>{item}</span>,
    )}
  </div>
);

export default LandingPage;
